import React from 'react';
import {
  AgnesButton,
  AgnesDataTable,
  AgnesIconButton,
  AgnesPageHeader,
  AgnesTabs,
  AgnesTabsContent,
  AgnesTabsList,
  AgnesTabsTrigger,
  AgnesTooltip,
} from '@agnes/ui';
import {
  ChevronDown,
  ChevronUp,
  CircleAlert,
  ClipboardList,
  Download,
  History,
  Info,
  Pencil,
  Trash2,
} from 'lucide-react';
import { ExportButton } from '@/components/ExportButton';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import { CardTitle } from '@/components/ui/SectionCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { MatrizExposicao } from '@/components/analise/MatrizExposicao';
import { SearchInput } from '@/components/SearchInput';
import { usePodeEditar } from '@/hooks/usePodeEditar';
import MENU_PERMISSIONS from '@/constants/ConstantMenuRoles';
import { type LinhaCbo, type LinhaPesquisa, useCboStore } from '@/stores/useCboStore';
import { useEmpregadoresStore } from '@/stores/useEmpregadoresStore';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { type Barra } from '@/lib/cbo';
import { comQaNasCelulas, qaKey } from '@/lib/dataQa';
import { countFiltros } from '@/lib/filtros';
import { isPerfilAnalista } from '@/utils/perfil';
import { employerListMessage } from '@/utils/employerListMessage';
import { SelectInline } from '@/components/SelectInline';
import { EmployerListRetryButton } from '@/components/EmployerListRetryButton';
import CboFilter from './CboFilter';
import { useNavigate } from 'react-router-dom';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

// CON-5399: botão "Exportar (visão do cliente)" oculto na versão inicial (não deve ficar nesta
// tela). Religável pela env; volta na futura divisão de perfis.
const SHOW_CBO_EXPORT_CLIENTE = import.meta.env.VITE_ENABLE_CBO_EXPORT_CLIENTE === 'true';

// linha de estatística: label + % · dot · barra · count/total
function StatRow({
  label,
  count,
  total,
  cor,
  dec = 2,
  dataQa,
}: Omit<Barra, 'key' | 'valor'> & { total: number; dec?: number; dataQa: string }) {
  const p = total ? (count / total) * 100 : 0;
  const pctTxt = p === 100 ? '100%' : `${p.toFixed(dec)}%`;
  return (
    <div data-qa={dataQa} className="flex items-center gap-3 py-1.5 text-sm">
      <span data-qa={`${dataQa}-rotulo`} className="w-44 shrink-0 text-right text-agnes-text-muted">
        {label} {pctTxt}
      </span>
      <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: cor }} />
      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-agnes-bg-muted">
        <div
          className="h-full rounded-full"
          style={{ width: `${p}%`, background: p ? cor : undefined }}
        />
      </div>
      <span
        data-qa={`${dataQa}-quantidade`}
        className="w-28 shrink-0 text-right"
        style={{ color: count > 0 ? cor : undefined }}
      >
        {count} / {total}
      </span>
    </div>
  );
}

const soma = (barras: Barra[]) => barras.reduce((acc, b) => acc + b.count, 0);

// card com título + info + conteúdo
function ChartCard({
  title,
  info,
  children,
  dataQa,
}: {
  title: string;
  info?: React.ReactNode;
  children: React.ReactNode;
  dataQa: string;
}) {
  return (
    <div data-qa={dataQa} className="rounded-xl border border-agnes-border p-4">
      <div className="mb-3 flex items-center justify-between">
        <CardTitle data-qa={`${dataQa}-titulo`}>{title}</CardTitle>
        {info && (
          <AgnesTooltip
            className="max-w-sm whitespace-normal"
            content={info}
            dataQa={`${dataQa}-info`}
          >
            <Info className="h-4 w-4 text-agnes-primary" />
          </AgnesTooltip>
        )}
      </div>
      {children}
    </div>
  );
}

const ETAPAS_ANALISE_INFO = (
  <div>
    <p className="font-semibold">Etapas de Análise:</p>
    <ul className="mt-1 list-disc space-y-0.5 pl-4">
      <li>
        <b>Analisar</b> - Itens importados mas ainda não analisados.
      </li>
      <li>
        <b>Esclarecimento</b> - O item requer esclarecimento para que a análise possa ser concluída.
      </li>
      <li>
        <b>Em análise</b> - item esta sendo analisado.
      </li>
      <li>
        <b>Apto para revisão</b> - item esta apto para a revisão técnica.
      </li>
      <li>
        <b>Revisado</b> - item esta revisado, mas o resultado ainda não foi disponibilizado ao
        cliente.
      </li>
      <li>
        <b>Analisado</b> - a análise foi concluída e o resultado esta visível ao cliente.
      </li>
    </ul>
  </div>
);

const STATUS_ANALISE_INFO = (
  <div>
    <p className="font-semibold">Status das Análises:</p>
    <ul className="mt-1 list-disc space-y-0.5 pl-4">
      <li>
        <b>Em análise</b> - item esta sendo analisado.
      </li>
      <li>
        <b>Esclarecimento</b> - O item requer esclarecimento para que a análise possa ser concluída.
      </li>
      <li>
        <b>Analisado</b> - a análise foi concluída e os resultados estão disponíveis.
      </li>
    </ul>
  </div>
);

const CLASSIFICACAO_ANALISE_INFO = (
  <div>
    <p className="font-semibold">Classificação das Análises:</p>
    <ul className="mt-1 list-disc space-y-0.5 pl-4">
      <li>
        <b>Em análise</b> - item esta sendo analisado.
      </li>
      <li>
        <b>Esclarecimento</b> - O item requer esclarecimento para que a análise possa ser concluída.
      </li>
      <li>
        <b>Risco detectado</b> - O item foi analisado e foram identificados pontos de atenção
        verifique as recomendações no menu de ações do item.
      </li>
      <li>
        <b>Inexpressivo</b> - O item foi analisado e não foram identificados pontos passíveis de
        crítica.
      </li>
    </ul>
  </div>
);

const EXPOSICAO_RISCO_INFO =
  'O gráfico de riscos considera apenas os itens analisados e que possuem algum risco relacionado. Os itens com risco são distribuídos de acordo com o grau de risco, para mais detalhes quanto os critérios de classificação de risco acesse a matriz de exposição.';

// card de gráficos colapsável (header + toggle Ocultar/Exibir)
function ChartsCard({
  title,
  children,
  dataQa,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  dataQa: string;
}) {
  const [show, setShow] = React.useState(true);
  return (
    <div
      data-qa={dataQa}
      className="mt-4 rounded-xl border border-agnes-border bg-agnes-bg-default shadow-agnes-card"
    >
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2 text-agnes-primary">
          <span data-qa={`${dataQa}-titulo`} className="font-medium">
            {title}
          </span>
        </div>
        <button
          type="button"
          data-qa={`${dataQa}-toggle`}
          className="flex items-center gap-1 text-sm font-medium text-agnes-primary"
          onClick={() => setShow((s) => !s)}
        >
          {show ? 'Ocultar gráficos' : 'Exibir gráficos'}
          {show ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>
      {show && (
        <div data-qa={`${dataQa}-conteudo`} className="border-t border-agnes-border p-4">
          {children}
        </div>
      )}
    </div>
  );
}

// tabela de trabalhadores (Visão geral / Risco) — paginação server-side via store
function WorkersTable({ dataQa }: { dataQa: string }) {
  const {
    lista,
    total,
    page,
    size,
    sortBy,
    sortOrder,
    carregandoLista,
    busca,
    setBusca,
    buscar,
    setPage,
    setSize,
    setSort,
    exportando,
    exportar,
    exportandoClientView,
    exportarClientView,
    opcoesFiltro,
    setFiltros,
    filtros,
  } = useCboStore();
  const { hiddenColumnKeys, toggleColumn } = useColumnPreferences('trabalhador-cbo');
  const navigate = useNavigate();

  const podeEditar = usePodeEditar(MENU_PERMISSIONS.CBO_EDITAR);

  // A edição da análise é uma página dedicada (não mais um modal); a própria página
  // carrega a análise pelo :id da rota.
  const abrirEdicao = (idTrabalhador: number | null) => {
    if (idTrabalhador == null) return;
    navigate(`/esocial/trabalhador/cbo/analise/${idTrabalhador}`);
  };
  const abrirHistorico = (idTrabalhador: number | null) => {
    if (idTrabalhador == null) return;
    navigate(`/esocial/trabalhador/cbo/analise/${idTrabalhador}?tab=historico`);
  };

  const columns = [
    {
      key: 'idx',
      header: '',
      width: 48,
      toggleable: false,
      render: (r: LinhaCbo) => (
        <span className="text-agnes-text-muted">{lista.indexOf(r) + 1 + page * size}</span>
      ),
    },
    {
      key: 'empresa',
      header: 'Empregador',
      width: 260,
      sortable: true,
      render: (r: LinhaCbo) => (
        <div className="leading-tight">
          <p className="text-agnes-text">{r.empresa}</p>
          <p className="text-agnes-text-muted">{r.cnpj}</p>
        </div>
      ),
    },
    { key: 'cpf', header: 'CPF', width: 130, sortable: true },
    {
      key: 'matricula',
      header: 'Matrícula',
      width: 130,
      sortable: true,
      render: (r: LinhaCbo) => (
        <span className="block max-w-[120px] truncate" title={r.matricula}>
          {r.matricula}
        </span>
      ),
    },
    { key: 'nome', header: 'Nome / Nome social', width: 220, sortable: true },
    {
      key: 'pcd',
      header: 'PCD',
      width: 80,
      render: (r: LinhaCbo) => r.pcd || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'situacaoContratual',
      header: 'Situação contratual',
      width: 150,
      render: (r: LinhaCbo) =>
        r.situacaoContratual || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'inicioContrato',
      header: 'Início do contrato',
      width: 140,
      render: (r: LinhaCbo) => r.inicioContrato || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'setor',
      header: 'Setor',
      width: 180,
      sortable: true,
      render: (r: LinhaCbo) =>
        r.setor ? (
          <span className="block max-w-[160px] truncate" title={r.setor}>
            {r.setor}
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-agnes-error">
            <CircleAlert className="h-4 w-4 shrink-0" /> Sem preenchimento
          </span>
        ),
    },
    {
      key: 'cargo',
      header: 'Cargo',
      width: 160,
      sortable: true,
      render: (r: LinhaCbo) => (
        <span className="block max-w-[150px] truncate" title={r.cargo}>
          {r.cargo}
        </span>
      ),
    },
    { key: 'etapa', header: 'Etapa da análise', width: 150, sortable: true },
    { key: 'classificacao', header: 'Classificação', width: 150, sortable: true },
    {
      key: 'risco',
      header: 'Grau de risco',
      width: 130,
      sortable: true,
      render: (r: LinhaCbo) => r.risco || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'ocupacao',
      header: 'Ocupação',
      width: 240,
      sortable: true,
      render: (r: LinhaCbo) => (
        <span className="block max-w-[220px] truncate" title={r.ocupacao}>
          {r.ocupacao}
        </span>
      ),
    },
    {
      key: 'diagnostico',
      header: 'Diagnóstico (Recomendação Agnes)',
      width: 200,
      render: (r: LinhaCbo) => r.diagnostico || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'ocupacaoRecomendada',
      header: 'Ocupação (Recomendação Agnes)',
      width: 240,
      render: (r: LinhaCbo) => (
        <span className="block max-w-[220px] truncate" title={r.ocupacaoRecomendada}>
          {r.ocupacaoRecomendada || <span className="text-agnes-text-muted">-</span>}
        </span>
      ),
    },
    {
      key: 'descricaoAtividades',
      header: 'Descrição das atividades',
      width: 240,
      render: (r: LinhaCbo) => (
        <span className="block max-w-[220px] truncate" title={r.descricaoAtividades}>
          {r.descricaoAtividades || <span className="text-agnes-text-muted">-</span>}
        </span>
      ),
    },
    {
      key: 'descricaoAtividadesRecomendada',
      header: 'Descrição das atividades (Recomendação Agnes)',
      width: 240,
      render: (r: LinhaCbo) => (
        <span className="block max-w-[220px] truncate" title={r.descricaoAtividadesRecomendada}>
          {r.descricaoAtividadesRecomendada || <span className="text-agnes-text-muted">-</span>}
        </span>
      ),
    },
    {
      key: 'descricaoSumaria',
      header: 'Descrição sumária',
      width: 240,
      render: (r: LinhaCbo) => (
        <span className="block max-w-[220px] truncate" title={r.descricaoSumaria}>
          {r.descricaoSumaria || <span className="text-agnes-text-muted">-</span>}
        </span>
      ),
    },
    {
      key: 'descricaoSumariaRecomendada',
      header: 'Descrição sumária (Recomendação Agnes)',
      width: 240,
      render: (r: LinhaCbo) => (
        <span className="block max-w-[220px] truncate" title={r.descricaoSumariaRecomendada}>
          {r.descricaoSumariaRecomendada || <span className="text-agnes-text-muted">-</span>}
        </span>
      ),
    },
    {
      key: 'grauInstrucao',
      header: 'Grau de instrução',
      width: 180,
      render: (r: LinhaCbo) => r.grauInstrucao || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'escolaridadeMinima',
      header: 'Escolaridade mínima',
      width: 180,
      render: (r: LinhaCbo) =>
        r.escolaridadeMinima || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'cotaAprendizagem',
      header: 'Cota de aprendizagem',
      width: 150,
      render: (r: LinhaCbo) =>
        r.cotaAprendizagem || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'orgaoClasse',
      header: 'Órgão de classe',
      width: 180,
      render: (r: LinhaCbo) => r.orgaoClasse || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'ambienteS2240',
      header: 'Ambiente de trabalho (S-2240)',
      width: 200,
      render: (r: LinhaCbo) => r.ambienteS2240 || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'ambienteS2200S2206',
      header: 'Local de trabalho (S-2200/S-2206)',
      width: 200,
      render: (r: LinhaCbo) =>
        r.ambienteS2200S2206 || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'inicioS2240',
      header: 'Início (S-2240)',
      width: 140,
      render: (r: LinhaCbo) => r.inicioS2240 || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'fimS2240',
      header: 'Fim (S-2240)',
      width: 140,
      render: (r: LinhaCbo) => r.fimS2240 || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'regimePrevidenciario',
      header: 'Regime previdenciário',
      width: 180,
      render: (r: LinhaCbo) =>
        r.regimePrevidenciario || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'versao',
      header: 'Versão',
      width: 100,
      render: (r: LinhaCbo) => r.versao || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'fimContrato',
      header: 'Fim do contrato',
      width: 140,
      render: (r: LinhaCbo) => r.fimContrato || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'categoria',
      header: 'Categoria',
      width: 200,
      render: (r: LinhaCbo) => (
        <span className="block max-w-[180px] truncate" title={r.categoria}>
          {r.categoria || <span className="text-agnes-text-muted">-</span>}
        </span>
      ),
    },
    {
      key: 'idEventoDadosContratuais',
      header: 'ID do evento de dados contratuais',
      width: 130,
      render: (r: LinhaCbo) =>
        r.idEventoDadosContratuais ?? <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'acoes',
      header: 'Ações',
      width: 100,
      align: 'center' as const,
      toggleable: false,
      render: (r: LinhaCbo) => (
        <div className="flex items-center justify-center gap-1">
          {podeEditar && (
            <AgnesIconButton
              aria-label="Editar análise"
              size="sm"
              title="Editar análise"
              dataQa={`${dataQa}-acao-editar-${r._key}`}
              onClick={() => abrirEdicao(r.idTrabalhador)}
            >
              <Pencil className="h-4 w-4" />
            </AgnesIconButton>
          )}
          <AgnesIconButton
            aria-label="Histórico da análise"
            size="sm"
            title="Histórico da análise"
            dataQa={`${dataQa}-acao-historico-${r._key}`}
            onClick={() => abrirHistorico(r.idTrabalhador)}
          >
            <History className="h-4 w-4" />
          </AgnesIconButton>
        </div>
      ),
    },
  ];

  const cols = comQaNasCelulas(dataQa, columns, (r) => r._key);

  return (
    <div data-qa={`${dataQa}-card`} className="mt-4 rounded-xl border border-agnes-border p-4">
      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-[320px] flex-1">
          <SearchInput
            dataQa={`${dataQa}-busca`}
            value={busca}
            onValueChange={setBusca}
            onSearch={buscar}
            placeholder="Pesquisar por empregador, CPF, matrícula, nome, cargo, ocupação..."
          />
        </div>
        <CboFilter opcoes={opcoesFiltro} onApply={setFiltros} count={countFiltros(filtros)} />
        <ColumnsMenu
          columns={columns}
          hiddenColumnKeys={hiddenColumnKeys}
          onToggleColumn={toggleColumn}
          data-qa={`${dataQa}-colunas`}
        />
        <ExportButton
          dataQa={`${dataQa}-exportar`}
          variant="outlined"
          startIcon={<Download className="h-4 w-4" />}
          disabled={exportando}
          onClick={exportar}
        >
          Exportar
        </ExportButton>
        {/* CON-5399: oculto via VITE_ENABLE_CBO_EXPORT_CLIENTE (volta na divisão de perfis) */}
        {SHOW_CBO_EXPORT_CLIENTE && (
          <ExportButton
            dataQa={`${dataQa}-exportar-cliente`}
            variant="outlined"
            startIcon={<Download className="h-4 w-4" />}
            disabled={exportandoClientView}
            onClick={exportarClientView}
            aria-label="Exportar relatório (visão do cliente)"
          >
            Exportar (visão do cliente)
          </ExportButton>
        )}
      </div>
      {!carregandoLista && lista.length === 0 ? (
        <EmptyState
          dataQa={`${dataQa}-vazio`}
          className="mt-4"
          title="Nenhum trabalhador para exibir"
        />
      ) : (
        <TableWrapper className="mt-4">
          <AgnesDataTable<LinhaCbo>
            dataQa={dataQa}
            columns={cols}
            hiddenColumnKeys={hiddenColumnKeys}
            data={lista}
            rowKey={(r) => r._key}
            loading={carregandoLista}
            sort={{ sortBy, sortOrder, onSortChange: setSort }}
            pagination={{
              page,
              pageSize: size,
              total,
              serverSide: true,
              onPageChange: setPage,
              onPageSizeChange: setSize,
            }}
          />
        </TableWrapper>
      )}
    </div>
  );
}

// bloco colapsável de instruções de pesquisa (texto fiel ao legado)
function InstrucoesPesquisa() {
  const [open, setOpen] = React.useState(true);
  return (
    <div
      data-qa="cbo-pesquisa-instrucoes"
      className="mt-4 rounded-xl border border-agnes-primary/30 bg-agnes-primary/5"
    >
      <button
        type="button"
        data-qa="cbo-pesquisa-instrucoes-toggle"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3 text-agnes-primary"
      >
        <span
          data-qa="cbo-pesquisa-instrucoes-titulo"
          className="flex items-center gap-2 font-medium"
        >
          <Info className="h-4 w-4" /> Instruções de pesquisa
        </span>
        <span className="flex items-center gap-1 text-sm font-medium">
          {open ? 'Ocultar detalhes' : 'Exibir detalhes'}
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {open && (
        <div
          data-qa="cbo-pesquisa-instrucoes-conteudo"
          className="space-y-3 border-t border-agnes-primary/20 px-6 py-4 text-sm text-agnes-text"
        >
          <p className="font-semibold">Para obter os melhores resultados, siga as orientações:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              A pesquisa não deve ser feita exclusivamente com números e caracteres especiais; deve
              incluir texto.
            </li>
            <li>
              Forneça informações detalhadas sobre as atividades realizadas pelo trabalhador,
              incluindo descrições precisas das tarefas físicas ou mentais executadas sob o comando
              a que está sujeito. As atividades devem ser descritas de forma exata e sucinta,
              utilizando verbos no infinitivo impessoal. Exemplos: &ldquo;Distribuir
              panfletos&rdquo;, &ldquo;Operar máquina de envase&rdquo;, etc.
            </li>
            <li>Evite usar descrições obtidas de websites.</li>
          </ul>
          <p className="font-semibold">Importante:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Atente-se para os CBOs que requerem alguma formação específica ou ainda registro em
              órgão de classe;
            </li>
            <li>
              As sugestões fornecidas são recomendações baseadas nas descrições fornecidas. O devido
              enquadramento das atividades são de responsabilidade do empregador. Em caso de
              dúvidas, entre em contato via chat ou seusucesso@agnes.com.br.
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

// aba Pesquisa: busca por descrição das atividades -> tabela de ocupações compatíveis
function TabPesquisa() {
  const {
    searchResults,
    searchTotal,
    carregandoBusca,
    pesquisar,
    exportandoPesquisa,
    exportarPesquisa,
  } = useCboStore();
  const [term, setTerm] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const { hiddenColumnKeys, toggleColumn } = useColumnPreferences('trabalhador-cbo-pesquisa');

  const columns = [
    { key: 'idColumn', header: 'ID', width: 60 },
    {
      key: 'compatibility',
      header: 'Compatibilidade',
      width: 160,
      render: (r: LinhaPesquisa) => (
        <span className="inline-flex items-center gap-1.5">
          {r.compatibility === '100%' && <CircleAlert className="h-4 w-4 text-agnes-warning" />}
          {r.compatibility}
        </span>
      ),
    },
    { key: 'occupation', header: 'Ocupação', width: 320 },
    {
      key: 'education',
      header: 'Escolaridade mínima',
      width: 220,
      render: (r: LinhaPesquisa) => r.education || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'professionalRegulatoryBody',
      header: 'Órgão de classe',
      width: 200,
      render: (r: LinhaPesquisa) =>
        r.professionalRegulatoryBody || <span className="text-agnes-text-muted">-</span>,
    },
  ];

  const cols = comQaNasCelulas('cbo-pesquisa-tabela', columns, (r) => String(r.idColumn));

  return (
    <div data-qa="cbo-pesquisa">
      <div className="mx-auto max-w-3xl">
        <SearchInput
          dataQa="cbo-pesquisa-busca"
          value={term}
          onValueChange={setTerm}
          onSearch={(v) => {
            setPage(0);
            pesquisar(v);
          }}
          placeholder="Pesquisar por descrição das atividades do trabalhador"
        />
        {searchTotal === 0 && !carregandoBusca && <InstrucoesPesquisa />}
      </div>
      {(searchResults.length > 0 || carregandoBusca) && (
        <div
          data-qa="cbo-pesquisa-tabela-card"
          className="mt-4 rounded-xl border border-agnes-border p-4"
        >
          <div className="mb-3 flex justify-end gap-3">
            <ColumnsMenu
              columns={columns}
              hiddenColumnKeys={hiddenColumnKeys}
              onToggleColumn={toggleColumn}
              data-qa="cbo-pesquisa-colunas"
            />
            <ExportButton
              dataQa="cbo-pesquisa-exportar"
              variant="outlined"
              startIcon={<Download className="h-4 w-4" />}
              disabled={exportandoPesquisa || searchResults.length === 0}
              onClick={exportarPesquisa}
            >
              Exportar
            </ExportButton>
          </div>
          <div className="overflow-x-auto">
            <AgnesDataTable<LinhaPesquisa>
              dataQa="cbo-pesquisa-tabela"
              columns={cols}
              hiddenColumnKeys={hiddenColumnKeys}
              data={searchResults}
              rowKey={(r) => String(r.idColumn)}
              loading={carregandoBusca}
              pagination={{
                page,
                pageSize,
                total: searchTotal,
                onPageChange: setPage,
                onPageSizeChange: (s) => {
                  setPageSize(s);
                  setPage(0);
                },
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function PageTrabalhadorCBO() {
  const [tab, setTab] = React.useState('visao');
  const [subRisco, setSubRisco] = React.useState('exposicao');
  const [empregadorSel, setEmpregadorSel] = React.useState('');

  const empregadores = useEmpregadoresStore((s) => s.lista);
  const carregandoEmpregadores = useEmpregadoresStore((s) => s.carregando);
  const empregadoresCarregados = useEmpregadoresStore((s) => s.carregado);
  const erroEmpregadores = useEmpregadoresStore((s) => s.erro);
  const mensagemEmpregadores = employerListMessage({
    loading: carregandoEmpregadores,
    loaded: empregadoresCarregados,
    error: erroEmpregadores,
  });
  const opcoesEmpregador = React.useMemo(
    () =>
      empregadores.map((e) => ({
        value: e.cnpjFilial,
        label: e.company || e.fantasyName || e.cnpjFilial,
      })),
    [empregadores],
  );

  const {
    stepVM,
    statusVM,
    classificationVM,
    riskVM,
    riskTotal,
    aplicarEmpregador,
    carregarVisaoGeral,
    carregarRisco,
    carregarFiltros,
    limparPesquisa,
  } = useCboStore();

  // Perfil analista → gráfico de Etapa; cliente → gráfico de Status (paridade legado).
  const analista = React.useMemo(() => isPerfilAnalista(), []);

  React.useEffect(() => {
    useEmpregadoresStore.getState().buscar();
    carregarFiltros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (re)carrega os gráficos da aba ativa
  const recarregarAba = React.useCallback(
    (t: string) => {
      if (t === 'visao') carregarVisaoGeral(analista);
      else if (t === 'risco') carregarRisco();
    },
    [carregarVisaoGeral, carregarRisco, analista],
  );

  React.useEffect(() => {
    recarregarAba('visao');
  }, [recarregarAba]);

  const onTab = (t: string) => {
    if (tab === 'pesquisa' && t !== 'pesquisa') limparPesquisa();
    setTab(t);
    recarregarAba(t);
  };
  const aplicarFiltro = async () => {
    await aplicarEmpregador(empregadorSel);
    recarregarAba(tab);
  };
  const limparFiltro = async () => {
    setEmpregadorSel('');
    await aplicarEmpregador('');
    recarregarAba(tab);
  };

  const stepTotal = soma(stepVM);
  const statusTotal = soma(statusVM);
  const classTotal = soma(classificationVM);

  return (
    <>
      <AgnesPageHeader
        dataQa="cbo-cabecalho"
        icon={<ClipboardList />}
        title="Classificação Brasileira de Ocupações (CBO)"
        subtitle="Monitore e encontre recomendações de ocupações dos trabalhadores"
        breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Trabalhador' }, { label: 'CBO' }]}
      />

      <div className="mt-2 rounded-2xl border border-agnes-border bg-agnes-bg-surface">
        <AgnesTabs value={tab} onValueChange={onTab} dataQa="cbo-abas">
          <AgnesTabsList className="px-4 pt-2" dataQa="cbo-abas-lista">
            <AgnesTabsTrigger value="visao" dataQa="cbo-aba-visao">
              Visão geral
            </AgnesTabsTrigger>
            <AgnesTabsTrigger value="risco" dataQa="cbo-aba-risco">
              Risco
            </AgnesTabsTrigger>
            <AgnesTabsTrigger value="pesquisa" dataQa="cbo-aba-pesquisa">
              Pesquisa
            </AgnesTabsTrigger>
          </AgnesTabsList>

          {/* ── Visão geral ── */}
          <AgnesTabsContent value="visao" className="px-4 pb-4" dataQa="cbo-aba-visao-conteudo">
            <div className="flex flex-wrap items-center justify-end gap-3">
              <SelectInline
                dataQa="cbo-visao-empregador"
                placeholder="Empregador"
                value={empregadorSel}
                onValueChange={setEmpregadorSel}
                options={opcoesEmpregador}
                emptyText={mensagemEmpregadores}
              />
              <EmployerListRetryButton
                error={erroEmpregadores}
                onRetry={() => void useEmpregadoresStore.getState().buscar()}
                dataQa="cbo-visao-empregador-retry"
              />
              <AgnesButton
                variant="primary"
                onClick={aplicarFiltro}
                dataQa="cbo-visao-empregador-aplicar"
              >
                Aplicar filtro
              </AgnesButton>
              <AgnesButton
                variant="outlined"
                onClick={limparFiltro}
                dataQa="cbo-visao-empregador-limpar"
                startIcon={
                  <Trash2 className="h-4 w-4" data-qa="cbo-visao-empregador-limpar-icone" />
                }
              >
                Limpar filtro
              </AgnesButton>
            </div>

            <ChartsCard
              dataQa="cbo-visao-graficos"
              title={
                <>
                  Gráficos{' '}
                  <span className="font-normal">
                    {analista
                      ? 'por etapa de análise e classificação de análise'
                      : 'por status de análise e classificação de análise'}
                  </span>
                </>
              }
            >
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {analista ? (
                  <ChartCard
                    dataQa="cbo-grafico-etapas"
                    title="Etapas de Análise"
                    info={ETAPAS_ANALISE_INFO}
                  >
                    {stepVM.map((s) => (
                      <StatRow
                        key={s.key}
                        label={s.label}
                        count={s.count}
                        cor={s.cor}
                        total={stepTotal}
                        dataQa={`cbo-grafico-etapas-${qaKey(s.key)}`}
                      />
                    ))}
                  </ChartCard>
                ) : (
                  <ChartCard
                    dataQa="cbo-grafico-status"
                    title="Status de análise"
                    info={STATUS_ANALISE_INFO}
                  >
                    {statusVM.map((s) => (
                      <StatRow
                        key={s.key}
                        label={s.label}
                        count={s.count}
                        cor={s.cor}
                        total={statusTotal}
                        dataQa={`cbo-grafico-status-${qaKey(s.key)}`}
                      />
                    ))}
                  </ChartCard>
                )}
                <ChartCard
                  dataQa="cbo-grafico-classificacao"
                  title="Classificação de análise"
                  info={CLASSIFICACAO_ANALISE_INFO}
                >
                  {classificationVM.map((s) => (
                    <StatRow
                      key={s.key}
                      label={s.label}
                      count={s.count}
                      cor={s.cor}
                      total={classTotal}
                      dataQa={`cbo-grafico-classificacao-${qaKey(s.key)}`}
                    />
                  ))}
                </ChartCard>
              </div>
            </ChartsCard>

            <WorkersTable dataQa="cbo-visao-tabela" />
          </AgnesTabsContent>

          {/* ── Risco ── */}
          <AgnesTabsContent value="risco" className="px-4 pb-4" dataQa="cbo-aba-risco-conteudo">
            <div className="flex flex-wrap items-center justify-end gap-3">
              <SelectInline
                dataQa="cbo-risco-empregador"
                placeholder="Empregador"
                value={empregadorSel}
                onValueChange={setEmpregadorSel}
                options={opcoesEmpregador}
                emptyText={mensagemEmpregadores}
              />
              <EmployerListRetryButton
                error={erroEmpregadores}
                onRetry={() => void useEmpregadoresStore.getState().buscar()}
                dataQa="cbo-risco-empregador-retry"
              />
              <AgnesButton
                variant="primary"
                onClick={aplicarFiltro}
                dataQa="cbo-risco-empregador-aplicar"
              >
                Aplicar filtro
              </AgnesButton>
              <AgnesButton
                variant="outlined"
                onClick={limparFiltro}
                dataQa="cbo-risco-empregador-limpar"
                startIcon={
                  <Trash2 className="h-4 w-4" data-qa="cbo-risco-empregador-limpar-icone" />
                }
              >
                Limpar filtro
              </AgnesButton>
            </div>

            <ChartsCard
              dataQa="cbo-risco-graficos"
              title={
                <>
                  Gráficos <span className="font-normal">por exposição ao risco</span>
                </>
              }
            >
              <AgnesTabs value={subRisco} onValueChange={setSubRisco} dataQa="cbo-risco-subabas">
                <AgnesTabsList dataQa="cbo-risco-subabas-lista">
                  <AgnesTabsTrigger value="exposicao" dataQa="cbo-risco-subaba-exposicao">
                    Exposição ao risco
                  </AgnesTabsTrigger>
                  <AgnesTabsTrigger value="matriz" dataQa="cbo-risco-subaba-matriz">
                    Matriz de exposição
                  </AgnesTabsTrigger>
                </AgnesTabsList>
                <AgnesTabsContent
                  value="exposicao"
                  className="pt-4"
                  dataQa="cbo-risco-subaba-exposicao-conteudo"
                >
                  <div className="flex justify-end">
                    <AgnesTooltip
                      className="max-w-xs whitespace-normal"
                      content={EXPOSICAO_RISCO_INFO}
                      dataQa="cbo-risco-exposicao-info"
                    >
                      <Info className="h-4 w-4 text-agnes-primary" />
                    </AgnesTooltip>
                  </div>
                  {riskVM.length === 0 ? (
                    <p
                      data-qa="cbo-risco-exposicao-vazio"
                      className="py-8 text-center text-agnes-text-muted"
                    >
                      Sem trabalhadores com análise concluída.
                    </p>
                  ) : (
                    riskVM.map((s) => (
                      <StatRow
                        key={s.key}
                        label={s.label}
                        count={s.count}
                        cor={s.cor}
                        total={riskTotal}
                        dec={0}
                        dataQa={`cbo-grafico-risco-${qaKey(s.key)}`}
                      />
                    ))
                  )}
                </AgnesTabsContent>
                <AgnesTabsContent
                  value="matriz"
                  className="pt-4"
                  dataQa="cbo-risco-subaba-matriz-conteudo"
                >
                  <MatrizExposicao dataQa="cbo-risco-matriz" />
                </AgnesTabsContent>
              </AgnesTabs>
            </ChartsCard>

            <WorkersTable dataQa="cbo-risco-tabela" />
          </AgnesTabsContent>

          {/* ── Pesquisa ── */}
          <AgnesTabsContent
            value="pesquisa"
            dataQa="cbo-aba-pesquisa-conteudo"
            className="px-4 pb-4"
          >
            <TabPesquisa />
          </AgnesTabsContent>
        </AgnesTabs>
      </div>
    </>
  );
}
